import {FC, useCallback, useState} from 'react';
import {useTranslation} from 'react-i18next';
import CurrentTaskInfo from '../common/Info';
import {Button} from '../../../../common/Button';
import EmployeeSurveyQuestionnaire from './questionnaire';
import EnsurePermission from '../../../../common/EnsurePermission';
import permissionsInfo from '../../../../../constants/permissionsInfo';
import {taskStateToColor} from '../common/helpers/taskStateToColor';
import {CurrentTask} from '../../../types';

type EmployeeSurveyProps = {
  task: CurrentTask,
  isCurrentUserAssignee: boolean,
  isRunning: boolean,
  hasClaimButton: boolean,
  isSubmitting: boolean,
  handleOpenClaimPopup: () => void,
  alertRange: Nullable<number>,
  submitTask: (values: Dictionary<string | string[]>) => void
}

const EmployeeSurvey: FC<EmployeeSurveyProps> = ({
  task,
  isCurrentUserAssignee,
  isRunning,
  hasClaimButton,
  handleOpenClaimPopup,
  alertRange,
  submitTask,
  isSubmitting
}) => {
  const {t} = useTranslation(['tasks', 'common']);
  const [sectionColor, setSectionColor] = useState(taskStateToColor.NORMAL);
  const {formFields, answers} = task || {};

  const onChangeProgressBarStateCallback = useCallback((state: string) => {
    const nextColor = taskStateToColor[state];

    if (nextColor !== sectionColor) {
      setSectionColor(nextColor);
    }
  }, [sectionColor]);

  const commonProps = {
    isRunning,
    isCurrentUserAssignee
  };

  return (
    <div className={`employee-survey ${sectionColor}`}>
      <div className="employee-survey__header">
        <CurrentTaskInfo
          task={task}
          alertRange={alertRange}
          onChangeProgressBarStateCallback={onChangeProgressBarStateCallback}
          isWideProgressBar
          {...commonProps}
        />
      </div>

      <div className="employee-survey__questionnaire">
        {!!formFields?.length && (
          <EmployeeSurveyQuestionnaire
            formFields={formFields}
            answers={answers}
            submitTask={submitTask}
            isSubmitting={isSubmitting}
            {...commonProps}
          />
        )}
      </div>

      {hasClaimButton && (
        <EnsurePermission elementPermissions={permissionsInfo.tasks.claim}>
          <div className="current-task__button-container survey">
            <Button
              label={t('claim.btn')}
              onClick={handleOpenClaimPopup}
            />
          </div>
        </EnsurePermission>
      )}

      {isCurrentUserAssignee && isRunning && !formFields?.length && (
        <EnsurePermission
          elementPermissions={permissionsInfo.tasks.complete}
        >
          <div className="current-task__button-container">
            <Button
              label={t('common:complete')}
              type="button"
              onClick={() => submitTask({})}
              disabled={isSubmitting}
            />
          </div>
        </EnsurePermission>
      )}
    </div>
  );
};

export default EmployeeSurvey;
